import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useEntityStore } from '@/lib/sync/entity-store';
import type { RecordingMeta } from '@/lib/recordings/types/types';

/**
 * Returns the most recent `RecordingMeta` for a given `entityId`, or `null` if
 * none exist. Used by the document editor view to surface recording lifecycle
 * UI (banner, enhanced notes, transcript) inline with the doc body.
 *
 * Re-renders only when the recordings slice changes via shallow equality on
 * the projected array of values.
 */
export function useRecordingForDoc(
  entityId: string | null | undefined,
): RecordingMeta | null {
  const recordings = useEntityStore(
    useShallow((s) => Array.from(s.recordings.values())),
  );

  return useMemo(() => {
    if (!entityId) return null;
    // Filter nulls/undefined defensively — IndexedDB hydration has occasionally
    // produced sparse Map entries (root cause not yet identified). Without this
    // guard the editor crashes via AppErrorBoundary on any such row.
    const matches = recordings.filter(
      (r): r is RecordingMeta => r != null && r.entityId === entityId,
    );
    if (matches.length === 0) return null;
    // Most recent by startedAt
    return matches.sort((a, b) =>
      b.startedAt.localeCompare(a.startedAt),
    )[0];
  }, [recordings, entityId]);
}
